import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/card.js';

interface Task {
  id: string;
  beeName: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  payload?: any;
  result?: any;
  error?: string;
}

const TasksPage: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/orchestrator/tasks');
        if (!response.ok) {
          throw new Error(`Failed to fetch tasks: ${response.status}`);
        }
        const data = await response.json();
        setTasks(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
        console.error('Error fetching tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
    const interval = setInterval(fetchTasks, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'text-green-400';
      case 'failed': return 'text-red-400';
      case 'pending': return 'text-yellow-400';
      case 'running': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="h-full bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-8">Tasks & Metrics</h1>
          <div className="text-slate-300">Loading tasks...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full bg-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-8">Tasks & Metrics</h1>
          <div className="text-red-400">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8">Tasks & Metrics</h1>
        
        <Card className="glass-dark bg-gradient-to-br from-slate-900/80 to-slate-800/60 backdrop-blur-xl border border-slate-700/30 rounded-xl hover:border-slate-600/50 transition-all duration-300">
          <CardHeader>
            <CardTitle className="text-white">Recent Tasks ({tasks.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {tasks.length === 0 ? (
              <div className="text-slate-400">No tasks found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-600">
                      <th className="text-left py-2 px-3 text-slate-300">ID</th>
                      <th className="text-left py-2 px-3 text-slate-300">Bee</th>
                      <th className="text-left py-2 px-3 text-slate-300">Status</th>
                      <th className="text-left py-2 px-3 text-slate-300">Started</th>
                      <th className="text-left py-2 px-3 text-slate-300">Completed</th>
                      <th className="text-left py-2 px-3 text-slate-300">Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((task) => (
                      <tr key={task.id} className="border-b border-slate-700 hover:bg-slate-700/50">
                        <td className="py-2 px-3 text-slate-300 font-mono text-xs">{task.id?.slice(0, 8)}...</td>
                        <td className="py-2 px-3 text-slate-300">{task.beeName}</td>
                        <td className={`py-2 px-3 font-medium ${getStatusColor(task.status)}`}>{task.status}</td>
                        <td className="py-2 px-3 text-slate-400">{formatDate(task.startedAt)}</td>
                        <td className="py-2 px-3 text-slate-400">{task.completedAt ? formatDate(task.completedAt) : 'N/A'}</td>
                        <td className="py-2 px-3 text-red-400 text-xs">{task.error ? task.error.slice(0, 50) + '...' : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TasksPage;
